import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { LogOut, Menu, Send } from "lucide-react";
import LogoutModal from "./LogoutModal";
import NotificationBell from "./NotificationBell";
import AdminBroadcastModal from "../admin/AdminBroadcastModal";
import ThemeToggle from "./ThemeToggle";

const AdminHeader = () => {
  const { user, logout } = useAuth();
  const { toggleAdminMobileSidebar } = useSidebar();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-[10000] bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800 transition-colors">
      <div className="px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleAdminMobileSidebar}
              className="lg:hidden p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Logo */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-medium text-xs sm:text-sm">PB</span>
              </div>
              <span className="text-lg sm:text-xl font-medium text-gray-900 dark:text-gray-100">
                PhotoBooth
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <ThemeToggle size="sm" />

            {/* Broadcast Button */}
            <button
              onClick={() => setShowBroadcastModal(true)}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center space-x-1"
              title="Send Broadcast"
            >
              <Send className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline text-sm">Broadcast</span>
            </button>

            {/* Notification Bell */}
            <NotificationBell />

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-xs sm:text-sm">
                    {user?.name ? user.name.charAt(0).toUpperCase() : "A"}
                  </span>
                </div>
                {/* <span className="text-sm font-normal hidden md:block">
                  {user?.name || "Admin"}
                </span> */}
              </button>

              {/* User Dropdown */}
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white dark:bg-gray-900 rounded-md shadow-lg py-1 z-[10001] border border-gray-200 dark:border-gray-700">
                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800">
                    <p className="text-sm font-normal text-gray-900 dark:text-gray-100">
                      {user?.name || "Admin"}
                    </p>
                    <p className="text-xs font-light text-gray-500 dark:text-gray-400">
                      {user?.email || "admin@example.com"}
                    </p>
                  </div>
                  <div className="px-2 py-1 border-b border-gray-100 dark:border-gray-800 sm:hidden">
                    <ThemeToggle showLabel className="w-full justify-start px-2" />
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />

      {/* Broadcast Modal */}
      <AdminBroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
      />
    </header>
  );
};

export default AdminHeader;
